import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { IconAlertCircle, IconCircleCheck, IconLoader2, IconX } from '@tabler/icons-react';
import api from '@/lib/api';
import MainCard from '@/components/MainCard';
import { Alert, AlertAction, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface EntityData {
  uuid: string;
  entityType: 'Company' | 'Trust';
  companyCategory: string | null;
  registeredName: string;
  registrationNumber: string | null;
  tradingName: string | null;
  incomeTaxNumber: string | null;
  vatNumber: string | null;
  registeredAddress: any;
  businessAddress: any;
}

export default function EntityCompletePage() {
  const { entityId } = useParams<{ entityId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [entity, setEntity] = useState<EntityData | null>(null);

  // Form state
  const [registeredName, setRegisteredName] = useState('');
  const [tradingName, setTradingName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [incomeTaxNumber, setIncomeTaxNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Access token is required. Please use the link provided in your email.');
      setLoading(false);
      return;
    }

    if (!entityId) {
      setError('Entity ID is required.');
      setLoading(false);
      return;
    }

    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadEntity is not wrapped in useCallback; runs when token/entityId change
  }, [token, entityId]);

  const loadEntity = async () => {
    try {
      setError(null);
      // Use token as query parameter for token-based authentication
      const response = await api.get(`/legal-entities/public/${entityId}?token=${token}`);
      const entityData = response.data.data;
      setEntity(entityData);
      setRegisteredName(entityData.registeredName || '');
      setTradingName(entityData.tradingName || '');
      setRegistrationNumber(entityData.registrationNumber || '');
      setIncomeTaxNumber(entityData.incomeTaxNumber || '');
      setVatNumber(entityData.vatNumber || '');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load entity details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !entityId) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Update basic entity information - token passed as query parameter
      await api.patch(`/legal-entities/public/${entityId}?token=${token}`, {
        entity_type: entity?.entityType,
        company_category: entity?.companyCategory,
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save changes';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto mt-8 max-w-3xl">
        <MainCard className="text-center">
          <IconLoader2 className="mx-auto size-8 animate-spin text-muted-foreground" aria-hidden />
          <p className="mt-2 text-sm text-muted-foreground">Loading entity details...</p>
        </MainCard>
      </div>
    );
  }

  if (error && !entity) {
    return (
      <div className="mx-auto mt-8 max-w-3xl">
        <MainCard>
          <Alert variant="destructive">
            <IconAlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <p className="mt-2 text-sm text-muted-foreground">
            If you believe this is an error, please contact support or request a new access link.
          </p>
        </MainCard>
      </div>
    );
  }

  return (
    <div className="mx-auto mb-8 mt-8 max-w-3xl">
      <MainCard>
        <h1 className="text-2xl font-semibold tracking-tight">Complete Entity Details</h1>
        <p className="mb-6 mt-1 text-sm text-muted-foreground">
          Please complete the required information for {entity?.registeredName || 'your entity'}.
        </p>

        {error && (
          <Alert variant="destructive" className="relative mb-6">
            <IconAlertCircle />
            <AlertDescription>{error}</AlertDescription>
            <AlertAction>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setError(null)}
                aria-label="Dismiss error"
              >
                <IconX />
              </Button>
            </AlertAction>
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-6">
            <IconCircleCheck />
            <AlertDescription>
              Your changes have been saved successfully. Thank you for completing the form!
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6">
            <div className="grid gap-2">
              <Label htmlFor="entity-registered-name">Registered Name</Label>
              <Input
                id="entity-registered-name"
                value={registeredName}
                onChange={(e) => setRegisteredName(e.target.value)}
                required
                disabled
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="entity-trading-name">Trading Name</Label>
              <Input
                id="entity-trading-name"
                value={tradingName}
                onChange={(e) => setTradingName(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="entity-registration-number">Registration Number</Label>
              <Input
                id="entity-registration-number"
                value={registrationNumber}
                onChange={(e) => setRegistrationNumber(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="entity-income-tax">Income Tax Number</Label>
              <Input
                id="entity-income-tax"
                value={incomeTaxNumber}
                onChange={(e) => setIncomeTaxNumber(e.target.value)}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="entity-vat">VAT Number</Label>
              <Input id="entity-vat" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} />
            </div>

            <div className="mt-2 flex flex-row flex-wrap gap-2">
              <Button type="submit" disabled={saving} className={cn('min-w-[120px]', saving && 'gap-2')}>
                {saving ? (
                  <>
                    <IconLoader2 className="size-6 animate-spin" aria-hidden />
                    <span className="sr-only">Saving</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => loadEntity()} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        </form>

        <div className="mt-8 border-t border-border pt-6">
          <p className="text-xs text-muted-foreground">
            <strong>Note:</strong> This access link expires in 7 days. If you need more time or a new link,
            please contact support.
          </p>
        </div>
      </MainCard>
    </div>
  );
}
